import React, { Suspense, useEffect, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchKundaliniChart, fetchSkyView } from "../lib/api";
import { findNearestCity } from "../lib/cities";
import MapPicker from "./MapPicker";
import KundaliniChart from "./KundaliniChart";
import AstralWorldLoading from "./AstralWorld/Loading";

// Lazy-loaded — this is the only place in the app that imports the
// Astral World's three.js/R3F/GSAP/postprocessing stack, so webpack emits
// it as a separate chunk that only downloads once a user actually opens
// this Sky View tab, not on the main page's initial load.
const AstralWorld = React.lazy(() => import("./AstralWorld"));

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Defaults match the backend's own fallback (Hyderabad, IST) — see backend/config/settings.py.
const DEFAULT_LATITUDE = 17.3871;
const DEFAULT_LONGITUDE = 78.4917;

export default function KundaliniLookup({ open, onClose }) {
  const { lang, t } = useLanguage();
  const [viewMode, setViewMode] = useState("chart"); // "chart" | "sky"
  const [birthDate, setBirthDate] = useState(() => toISO(new Date()));
  const [birthTime, setBirthTime] = useState("12:00");
  const [latitude, setLatitude] = useState(DEFAULT_LATITUDE);
  const [longitude, setLongitude] = useState(DEFAULT_LONGITUDE);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [placeName, setPlaceName] = useState(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [kundaliniChart, setKundaliniChart] = useState(null);
  const [kundaliniLoading, setKundaliniLoading] = useState(false);
  const [kundaliniError, setKundaliniError] = useState("");
  const [skyData, setSkyData] = useState(null);
  const [subsolar, setSubsolar] = useState(null);
  const [skyLoading, setSkyLoading] = useState(false);
  const [skyError, setSkyError] = useState("");
  const [skyFullscreen, setSkyFullscreen] = useState(false);
  // Birth-location timezone isn't tracked anywhere in this form today (no
  // lat/lon -> tz-name lookup in the app) — the browser's own current
  // offset is the best available approximation, used consistently for both
  // the API calls below (previously omitted, silently relying on the
  // backend's own default) and the Astral World intro's info card.
  const [tzOffsetMin] = useState(() => -new Date().getTimezoneOffset());

  const label = (obj) => (obj ? (obj[lang] || obj.en || "") : "");

  useEffect(() => {
    const nearest = findNearestCity(latitude, longitude);
    if (!nearest) setPlaceName(null);
    else if (nearest.distanceKm < 5) setPlaceName(nearest.name);
    else if (nearest.distanceKm < 75) setPlaceName(`${t("locNear")} ${nearest.name}`);
    else setPlaceName(t("locCustom"));
  }, [latitude, longitude, t]);

  // Results are only valid for the view they were fetched under.
  const changeViewMode = (next) => {
    if (next === viewMode) return;
    setViewMode(next);
    setKundaliniChart(null);
    setKundaliniError("");
    setSkyData(null);
    setSubsolar(null);
    setSkyError("");
  };

  const confirmMapPick = ({ lat, lon }) => {
    setLatitude(Number(lat.toFixed(4)));
    setLongitude(Number(lon.toFixed(4)));
    setLocationError("");
    setMapPickerOpen(false);
  };

  const useMyLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationError(t("muhurtamLocationError"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(Number(pos.coords.latitude.toFixed(4)));
        setLongitude(Number(pos.coords.longitude.toFixed(4)));
        setLocating(false);
      },
      () => {
        setLocationError(t("muhurtamLocationError"));
        setLocating(false);
      },
      { timeout: 10000 }
    );
  };

  const showKundaliniChart = async () => {
    setKundaliniLoading(true);
    setKundaliniError("");
    setKundaliniChart(null);
    try {
      const chart = await fetchKundaliniChart({
        birth_date: birthDate,
        birth_time: birthTime,
        latitude,
        longitude,
        tz_offset_min: tzOffsetMin,
      });
      setKundaliniChart(chart);
    } catch (err) {
      setKundaliniError(t("kundaliniError"));
    } finally {
      setKundaliniLoading(false);
    }
  };

  // Astral World's main orbit scene is Kundali-accurate (sidereal), driven
  // by the same chart endpoint as the Chart tab — NOT the topocentric az/alt
  // sky-view endpoint's `planets` field (which would show real-time sky
  // position, not birth-chart position). Its `subsolar` field is still
  // exactly what's needed for the Phase 2 cinematic intro's real day/night
  // terminator, so both endpoints get called together here.
  const showSkyView = async () => {
    setSkyLoading(true);
    setSkyError("");
    setSkyData(null);
    setSubsolar(null);
    try {
      const payload = {
        birth_date: birthDate,
        birth_time: birthTime,
        latitude,
        longitude,
        tz_offset_min: tzOffsetMin,
      };
      const [chart, sky] = await Promise.all([fetchKundaliniChart(payload), fetchSkyView(payload)]);
      setSkyData(chart);
      setSubsolar(sky.subsolar);
      setSkyFullscreen(true);
    } catch (err) {
      setSkyError(t("kundaliniError"));
    } finally {
      setSkyLoading(false);
    }
  };

  const submit = viewMode === "chart" ? showKundaliniChart : showSkyView;
  const isLoading = viewMode === "chart" ? kundaliniLoading : skyLoading;

  return (
    <>
      {open && (
        <div className="dharma-panel kundalini-panel" data-testid="kundalini-panel">
          <div className="dharma-header">
            <div className="dharma-avatar">🔯</div>
            <div>
              <div className="dharma-title">{t("kundaliniSectionTitle")}</div>
              <div className="dharma-sub">{t("kundaliniTitle")}</div>
            </div>
            <button className="dharma-close" data-testid="kundalini-close" onClick={onClose}>&times;</button>
          </div>

          <div className="muhurtam-body">
            <div className="muhurtam-mode-row" role="tablist" aria-label={t("skyViewModeLabel")}>
              <button
                type="button"
                data-testid="kundalini-mode-chart"
                className={`muhurtam-mode-btn${viewMode === "chart" ? " muhurtam-mode-btn-active" : ""}`}
                onClick={() => changeViewMode("chart")}
              >
                {t("kundaliniModeChart")}
              </button>
              <button
                type="button"
                data-testid="kundalini-mode-sky"
                className={`muhurtam-mode-btn${viewMode === "sky" ? " muhurtam-mode-btn-active" : ""}`}
                onClick={() => changeViewMode("sky")}
              >
                {t("kundaliniModeSky")}
              </button>
            </div>
            {viewMode === "sky" && <div className="kundalini-note">{t("skyViewNote")}</div>}

            <div className="kundalini-note">{t("kundaliniNote")}</div>

            <div className="muhurtam-date-row">
              <label className="muhurtam-field">
                {t("muhurtamBirthDate")}
                <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
              </label>
              <label className="muhurtam-field">
                {t("muhurtamBirthTime")}
                <input type="time" value={birthTime} onChange={(e) => setBirthTime(e.target.value)} />
              </label>
            </div>

            <button
              type="button"
              className="dharma-send muhurtam-locate-btn"
              onClick={useMyLocation}
              disabled={locating}
            >
              {locating ? t("muhurtamLocating") : t("muhurtamUseLocation")}
            </button>
            <button
              type="button"
              className="dharma-send muhurtam-locate-btn"
              onClick={() => setMapPickerOpen(true)}
            >
              {t("locPickMap")}
            </button>
            {locationError && <div className="muhurtam-error">{locationError}</div>}
            {placeName && <div className="muhurtam-place-name">📍 {placeName}</div>}
            {mapPickerOpen && (
              <MapPicker
                initialLat={latitude}
                initialLon={longitude}
                onPick={confirmMapPick}
                onClose={() => setMapPickerOpen(false)}
                t={t}
              />
            )}

            <div className="muhurtam-date-row">
              <label className="muhurtam-field">
                {t("muhurtamLatitude")}
                <input
                  type="number" step="0.0001" min="-90" max="90"
                  value={latitude}
                  onChange={(e) => setLatitude(Number(e.target.value))}
                />
              </label>
              <label className="muhurtam-field">
                {t("muhurtamLongitude")}
                <input
                  type="number" step="0.0001" min="-180" max="180"
                  value={longitude}
                  onChange={(e) => setLongitude(Number(e.target.value))}
                />
              </label>
            </div>

            <button
              type="button"
              className="dharma-send muhurtam-submit"
              onClick={submit}
              disabled={isLoading}
            >
              {isLoading ? t("kundaliniLoading") : (viewMode === "chart" ? t("kundaliniShowChart") : t("skyViewShow"))}
            </button>

            {viewMode === "chart" && (
              <>
                {kundaliniError && <div className="muhurtam-error">{kundaliniError}</div>}
                {kundaliniChart && <KundaliniChart chart={kundaliniChart} label={label} t={t} />}
              </>
            )}
            {viewMode === "sky" && skyError && <div className="muhurtam-error">{skyError}</div>}
          </div>
        </div>
      )}
      {skyFullscreen && skyData && (
        <Suspense fallback={<AstralWorldLoading />}>
          <AstralWorld
            chart={skyData}
            label={label}
            t={t}
            onClose={() => setSkyFullscreen(false)}
            lat={latitude}
            lon={longitude}
            subsolar={subsolar}
            placeName={placeName}
            birthDate={birthDate}
            birthTime={birthTime}
            tzOffsetMin={tzOffsetMin}
          />
        </Suspense>
      )}
    </>
  );
}
